/**
 * Transaction reducer. Contains most of the relevant info for the moveMoney transaction, minus fees.
 */

import { ActionTypes as types } from '../constants'
import serverProps from '../lib/server-props'
import {
  isPhoneNumberMaybe,
  parseAndFormatPhoneNumber,
} from 'transfer/utils/phoneUtils'

// set initialState. This obj becomes immutable, since that's what Redux expects.
const initialState = {
  recipient: '',
  originAmount: '', // displayed origin amount
  destinationAmount: '', // displayed destination amount
  amount: '', // amount sent to core p2p
  totalCostCard: '0.00', // total cost of the transaction to the sender. Including fees
  totalCostDebit: '0.00', // total cost of the transaction to the sender. Including fees
  totalCostBank: '0.00', // total cost of the transaction to the sender. Including fees
  fromCurrencyCode: serverProps.currencyCode || 'USD',
  fromCountryCode: serverProps.country || 'US',
  toCurrencyCode: 'USD',
  // toCountryCode: 'DE',
  // toCountryName: 'Germany',
  // displayAmount: '$1,00 USD',
  rate: '1.0000',
  isPurchase: serverProps.isPurchase || false,
}

/**
 * Reducer function. This is a pure function (no side effects).
 * It makes state changes based on the action passed in, and then returns a new obj
 * with those mutations applied.
 */
export default function transaction(state = initialState, action) {
  switch (action.type) {
    case types.CHANGE_ORIGIN_COUNTRY:
      return {
        ...state, // ES7 obj spread. Same as Object.assign({}, state, fromCountry, fromCurrencyCode)
        fromCountryCode: action.data.fromCountryCode,
        fromCurrencyCode: action.data.fromCurrencyCode,
      }

    case types.CHANGE_ORIGIN_CURRENCY_CODE:
      return {
        ...state,
        originAmount: action.data.fromAmountPrecise,
        fromCurrencyCode: action.data.currencyCode,
      }

    case types.CHANGE_DESTINATION_CURRENCY_CODE:
      return {
        ...state,
        toCurrencyCode: action.data.currencyCode,
      }

    case types.CHANGE_RECIPIENT:
      return {
        ...state,
        recipient: action.data.recipient,
        isRecipientValid: action.data.isRecipientValid,
      }

    case types.CHANGE_DESTINATION_COUNTRY:
      return {
        ...state,
        toCountryLocalCurrency: action.data.localCurrency,
        toCountryCode: action.data.countryCode,
        toCurrencyCode: action.data.currencyCode,
        toCountryName: action.data.name,
      }

    case types.RECEIVED_DESTINATION_AMOUNT:
      return {
        ...state,
        rate: action.data.rate,
        destinationAmount: action.data.toAmountPrecise,
        displayAmount: action.data.toAmount, // used on the recipient page
      }

    case types.RECEIVED_ORIGIN_AMOUNT:
      return {
        ...state,
        rate: action.data.rate,
        originAmount: action.data.fromAmountPrecise,
        displayAmount: action.data.toAmount, // used on the recipient page
      }

    case types.CHANGE_AMOUNT:
      return {
        ...state,
        amount: action.data.amount,
      }

    case types.RECEIVED_EXCHANGE_RATE:
      return {
        ...state,
        rate: action.data.rate,
        // intentionally ignore everything else because
        // we used fake data to ensure we'd get a good
        // exchange rate (sending 0 would result in a
        // bad call)
      }

    case types.RECEIVED_FEE:
      return {
        ...state,
        totalCostBank: action.data.bank.totalCost,
        totalCostCard: action.data.card.totalCost,
        totalCostDebit: action.data.debit.totalCost,
      }

    case types.CHANGE_ORIGIN_AMOUNT:
      return {
        ...state,
        originAmount: action.data.amountPrecise,
        destinationAmount: action.data.amountIsZero
          ? '0'
          : state.destinationAmount,
      }

    case types.CHANGE_DESTINATION_AMOUNT:
      return {
        ...state,
        destinationAmount: action.data.amountPrecise,
        originAmount: action.data.amountIsZero ? '0' : state.originAmount,
      }

    case types.SELECT_CONTACT: {
      let {
        fullName = '',
        firstName = '',
        lastName = '',
        alternateFullName = '',
        companyName = '',
        displayName = '',
        profile_image,
        thumbnailUrl = '',
        email,
        phones = [],
        hasNetworkId = false,
        networkIdDisplayName = '',
      } = action.data.contact
      const { user: { country: senderCountryCode } = {} } = state

      if (profile_image && profile_image.image_url) {
        // choose the better thumbnail if possible
        thumbnailUrl = profile_image.image_url
      }

      const firstInitial =
        firstName.charAt(0) ||
        alternateFullName.charAt(0) ||
        companyName.charAt(0)
      const lastInitial = lastName.charAt(0)

      // todo break all of this logic out into a util (that uses griffin?) *search this comment to see everywhere*
      // pull out the first phone numebr
      let [phone = {}] = phones

      // pull out the phone_number property, which will be either object or string
      const { phone_number = {} } = phone

      // try to pull out the properties
      const { country_code = '', national_number = '' } = phone_number

      // if the properties exist, let's construct the string, else phone_number
      // is a string so just use that
      if (country_code && national_number) {
        phone = `+${country_code} ${national_number}`
      } else {
        phone = phone_number
      }

      let recipientDisplayDetails
      if (email) {
        recipientDisplayDetails = email

        // non-contact recipient form input is always passed as email
        if (isPhoneNumberMaybe(email)) {
          const formattedPhoneNumber = parseAndFormatPhoneNumber(
            email,
            senderCountryCode
          )
          if (formattedPhoneNumber) {
            recipientDisplayDetails = formattedPhoneNumber
          }
        }
      } else {
        recipientDisplayDetails = phone

        const formattedPhoneNumber = parseAndFormatPhoneNumber(
          phone,
          senderCountryCode
        )
        if (formattedPhoneNumber) {
          recipientDisplayDetails = formattedPhoneNumber
        }
      }

      const networkIdInfo = {
        recipientDisplayDetails,
      }
      if (hasNetworkId) {
        networkIdInfo.recipientDisplayDetails = networkIdDisplayName
      }

      if (alternateFullName) {
        displayName = alternateFullName
      } else if (fullName) {
        displayName = fullName.trim()
      }

      return {
        ...state,
        recipient: email || phone,
        isRecipientValid: true,
        displayName,
        firstName,
        lastName,
        firstInitial,
        lastInitial,
        thumbnailUrl,
        ...networkIdInfo,
      }
    }

    default:
      return state
  }
}
